import { readsApi } from './api';

// ── Ledger explorer: the "where is the money" read API ─────────────────────
// Every endpoint lives in the FastAPI reads service (:8000) under /api/v1 and
// is gated on the chekit-level `wallets.read` permission.

export type WalletOwnerType = 'system' | 'user' | 'business' | 'branch';

// Full wallet_transactions.transaction_type CHECK vocabulary (schema 07).
// The eight money-flow types the escrow engine actually emits are listed first.
export type LedgerTransactionType =
  | 'payment'
  | 'hold'
  | 'release'
  | 'settlement'
  | 'commission'
  | 'delivery_fee'
  | 'refund'
  | 'withdrawal'
  | 'deposit'
  | 'transfer_in'
  | 'transfer_out'
  | 'promotion_credit'
  | 'promotion_debit'
  | 'ad_spend'
  | 'ad_refund'
  | 'delivery_deduction';

// Types offered in the statement filter — the escrow money vocabulary.
export const MONEY_TRANSACTION_TYPES: LedgerTransactionType[] = [
  'payment',
  'hold',
  'release',
  'settlement',
  'commission',
  'delivery_fee',
  'refund',
  'withdrawal',
];

// A hold is consumed by any of these, not just an explicit `release`.
export const HOLD_RELEASE_TYPES: LedgerTransactionType[] = [
  'release',
  'settlement',
  'delivery_fee',
  'commission',
];

// wallet_types.code — the 14 seeded types.
export const WALLET_TYPE_CODES = [
  'chekit_main',
  'chekit_commission',
  'chekit_escrow',
  'chekit_promotion',
  'chekit_ad_revenue',
  'tax_float',
  'business_main',
  'business_promotion',
  'business_ad_spend',
  'branch_float',
  'personal',
  'savings',
  'customer_rewards',
  'rider_earnings',
] as const;

// ── Hold semantics ─────────────────────────────────────────────────────────
//
// `wallets.available_balance` is a GENERATED column: `balance - held_balance`.
// That is the BANKING meaning of "held" — a locked slice of money the wallet
// already contains.
//
// Chekit's escrow uses `held_balance` on a DESTINATION wallet to mean something
// else: money earmarked FOR you that is still sitting in `chekit_escrow`. The
// cash never entered the wallet (those hold rows carry `running_balance = 0`),
// so subtracting the earmark from a balance that does not contain it produces a
// meaningless negative. That is why raw `availableBalance` must never be the
// headline figure — read `spendable` instead, and `heldStranded` for the number
// that actually needs an operator.

/** Which kind of hold an outstanding earmark is. */
export type HoldClass = 'incoming' | 'stranded' | 'onBalance';

/** The four figures every wallet row, rollup and bucket now carries. */
export interface HoldBreakdown {
  /** Earmarked from escrow against a basket still IN FLIGHT. The money is in
   *  chekit_escrow, not here. Neutral — this is what a live order looks like. */
  heldIncoming: number;
  /** Earmarked against a basket that has ALREADY FINISHED. Nothing will ever
   *  release it. This is the only figure worth an alarm. */
  heldStranded: number;
  /** A hold that genuinely locks this wallet's own balance (an operator's
   *  treasury adjust-hold). Banking semantics. */
  heldOnBalance: number;
  /** What the holder can actually use now: `balance − heldOnBalance`, floored
   *  at 0. NOT `balance − held`. */
  spendable: number;
}

/** Plain-English copy the API ships alongside the figures, so the portal and
 *  the service never drift on what these numbers mean. */
export interface HeldSemantics {
  incoming: string;
  stranded: string;
  rawAvailable: string;
}

export const HELD_INCOMING_NOTE =
  'held in escrow against orders in flight — lands here when the buyer confirms';

export const HELD_STRANDED_NOTE =
  'holds against baskets that already finished — nothing will ever release them. ' +
  'Clear them from the treasury adjust-hold worklist.';

export const RAW_AVAILABLE_NOTE = 'raw available (balance − held)';

// ── Wallets ────────────────────────────────────────────────────────────────

export interface LedgerWallet extends HoldBreakdown {
  walletId: string;
  typeCode: string;
  typeName: string;
  ownerType: WalletOwnerType;
  ownerLabel: string | null;
  /** Present once the reads service exposes it — used to open the owner ledger.
   *  System wallets have no owner id. */
  ownerId?: string | null;
  balance: number;
  heldBalance: number;
  /** The raw generated column. Kept for auditability, demoted in the UI —
   *  see the hold-semantics note above. */
  availableBalance: number;
  currency: string;
  status: string;
  isSystem: boolean;
  // Legacy aliases the API also emits.
  walletTypeCode: string;
  walletTypeName: string;
  isActive: boolean;
}

/** `heldDrift` = stored held_balance − recomputed outstanding holds.
 *  Non-zero drift is what explains a negative available balance. */
export interface LedgerWalletDetail extends LedgerWallet {
  heldOutstanding: number;
  heldDrift: number;
}

/** Aggregated hold breakdown — the rollup counterpart of `HoldBreakdown`. */
export interface HoldBreakdownSums {
  sumHeldIncoming: number;
  sumHeldStranded: number;
  sumHeldOnBalance: number;
  sumSpendable: number;
}

export interface WalletTypeRollup extends HoldBreakdownSums {
  typeCode: string;
  typeName: string;
  count: number;
  sumBalance: number;
  sumHeld: number;
  sumAvailable: number;
}

export interface WalletTotals {
  byWalletType: WalletTypeRollup[];
  grandTotal: HoldBreakdownSums & {
    count: number;
    sumBalance: number;
    sumHeld: number;
    sumAvailable: number;
  };
}

export interface WalletListResponse {
  data: LedgerWallet[];
  total: number;
  page: number;
  size: number;
  totals: WalletTotals;
  heldSemantics?: HeldSemantics;
}

export interface WalletListParams {
  ownerType?: WalletOwnerType;
  walletTypeCode?: string;
  q?: string;
  negativeOnly?: boolean;
  page?: number;
  size?: number;
}

// ── Statement ──────────────────────────────────────────────────────────────

export interface WalletStatementRow {
  id: string;
  transactionType: LedgerTransactionType;
  amount: number;
  /** Wallet balance AFTER this transaction. */
  runningBalance: number;
  description: string | null;
  referenceType: string | null;
  referenceId: string | null;
  createdAt: string;
  journalEntryId: string | null;
  counterpartyWalletId: string | null;
  counterpartyLabel: string | null;
}

export interface WalletStatementResponse {
  data: WalletStatementRow[];
  total: number;
  page: number;
  size: number;
  order: 'asc' | 'desc';
}

export interface WalletStatementParams {
  transactionType?: LedgerTransactionType;
  /** ISO timestamp */
  from?: string;
  /** ISO timestamp */
  to?: string;
  /** `asc` for a running-balance chart. */
  order?: 'asc' | 'desc';
  page?: number;
  size?: number;
}

// ── Money trace ────────────────────────────────────────────────────────────

export interface TraceOrder {
  id: string;
  status: string;
  paymentStatus: string;
  totalAmount: number;
  customerId: string | null;
  createdAt: string | null;
}

export interface TraceBasket {
  id: string;
  businessId: string | null;
  businessName: string | null;
  status: string;
  totalAmount: number;
  confirmedAt: string | null;
}

export interface TracePaymentTransaction {
  id: string;
  amount: number;
  netAmount: number;
  status: string;
  direction: string | null;
  externalTransactionId: string | null;
  mpesaCheckoutRequestId: string | null;
  createdAt: string | null;
  completedAt: string | null;
  failureReason: string | null;
}

export interface JournalEntryLine {
  id: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  walletId: string | null;
  walletLabel: string | null;
  debitAmount: number;
  creditAmount: number;
  description: string | null;
}

export interface TraceJournalEntry {
  id: string;
  description: string | null;
  referenceType: string | null;
  referenceId: string | null;
  status: string;
  postedBy: string | null;
  postedAt: string | null;
  createdAt: string | null;
  metadata: Record<string, unknown> | null;
  lines: JournalEntryLine[];
  debitTotal: number;
  creditTotal: number;
  /** Double-entry invariant: debits must equal credits. */
  balanced: boolean;
}

export interface TraceWalletMovement {
  id: string;
  walletId: string;
  walletLabel: string | null;
  transactionType: LedgerTransactionType;
  amount: number;
  runningBalance: number;
  description: string | null;
  referenceType: string | null;
  referenceId: string | null;
  journalEntryId: string | null;
  counterpartyWalletId: string | null;
  counterpartyLabel: string | null;
  createdAt: string | null;
}

export interface MoneyTrace {
  order: TraceOrder;
  baskets: TraceBasket[];
  paymentTransactions: TracePaymentTransaction[];
  journalEntries: TraceJournalEntry[];
  walletMovements: TraceWalletMovement[];
  /** Which kind of id the caller supplied. A basket id resolves up to its
   *  parent order — the money only makes sense at order level. */
  resolvedFrom?: 'order' | 'basket';
  /** Set when a basket id was supplied: the basket to draw attention to. */
  focusBasketId?: string | null;
}

// ── Reconciliation anomalies ───────────────────────────────────────────────

/** A negative raw `availableBalance` is only a fault to the extent of
 *  `heldStranded` — the rest is normal incoming escrow. */
export interface NegativeAvailableRow extends HoldBreakdown {
  walletId: string;
  typeCode: string;
  ownerLabel: string | null;
  balance: number;
  heldBalance: number;
  availableBalance: number;
}

export interface UnbalancedEntryRow {
  entryId: string;
  description: string | null;
  debitTotal: number;
  creditTotal: number;
  postedAt: string | null;
}

export interface HeldDriftRow {
  walletId: string;
  typeCode: string;
  ownerLabel: string | null;
  storedHeld: number;
  recomputedHeld: number;
  drift: number;
}

export interface StaleHoldRow {
  walletId: string;
  typeCode: string;
  ownerLabel: string | null;
  basketId: string;
  basketStatus: string;
  amount: number;
  createdAt: string | null;
}

export interface AnomalyCheck<T> {
  count: number;
  rows: T[];
}

export interface ReconciliationAnomalies {
  checks: {
    negativeAvailable: AnomalyCheck<NegativeAvailableRow>;
    unbalancedJournalEntries: AnomalyCheck<UnbalancedEntryRow>;
    heldBalanceDrift: AnomalyCheck<HeldDriftRow>;
    staleHolds: AnomalyCheck<StaleHoldRow>;
  };
  generatedAt: string;
}

// ── Service: ledger explorer → FastAPI reads :8000 ─────────────────────────
export const ledgerService = {
  // Every wallet + a rollup that matches the active filters (ignores paging).
  listWallets: (params?: WalletListParams) =>
    readsApi.get<WalletListResponse>('/admin/wallets', params as Record<string, unknown>),

  // The platform's own wallets (escrow, commission, tax float, main, ...).
  listSystemWallets: () =>
    readsApi.get<LedgerWallet[]>('/admin/wallets/system'),

  getUserWallets: (userId: string) =>
    readsApi.get<LedgerWallet[]>(`/users/${userId}/wallets`),

  // Adds heldOutstanding (recomputed) + heldDrift on top of the wallet row.
  getWallet: (walletId: string) =>
    readsApi.get<LedgerWalletDetail>(`/admin/wallets/${walletId}`),

  getWalletTransactions: (walletId: string, params?: WalletStatementParams) =>
    readsApi.get<WalletStatementResponse>(
      `/admin/wallets/${walletId}/transactions`,
      params as Record<string, unknown>,
    ),

  // Full money trace for ONE order. Accepts a top-level order id OR a basket
  // id — a basket resolves up to its parent order and comes back flagged
  // (`resolvedFrom: 'basket'` + `focusBasketId`).
  getOrderMoneyTrace: (orderId: string) =>
    readsApi.get<MoneyTrace>(`/admin/money-trace/orders/${orderId}`),

  getAnomalies: () =>
    readsApi.get<ReconciliationAnomalies>('/admin/reconciliation/anomalies'),
};
